import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { RatingFilterValues, ReviewsInsight } from 'apis/merchant-gateway/insights/reviews/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { InfiniteTable } from 'modules/experience-core';

import { RatingsFilterPopover } from './RatingsFilterPopover';
import { VenueReviewsTable } from './VenueReviewsTable';
import { useVenueReviewsTableProps, VenueReviewsTableSortableColumnIDs } from './hooks/useVenueReviewsTableProps';
import { ContentRow, InsightResponsePaginated, RowType } from '../../types';
import SummaryTable from '../InsightSummary/SummaryTable';

const SummaryTableStyled = styled(SummaryTable)`
  min-height: 648px;
`;

export const VenueReviewsCard = ({
  reviewsInsights,
  venue,
  sort,
  setSort,
  filters,
  setFilters,
}: {
  reviewsInsights: InsightResponsePaginated<ReviewsInsight>;
  venue: Venue;
  sort: InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>;
  setSort: (sort: InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>) => void;
  filters: RatingFilterValues[];
  setFilters: (value: RatingFilterValues[]) => void;
}) => {
  const intl = useIntl();

  const tableProps = useVenueReviewsTableProps({
    venue,
    sort,
    setSort,
    reviewsInsights,
  });

  const rows: ContentRow[] = useMemo(
    () => [
      {
        type: RowType.Raw,
        key: 'raw',
        content: <VenueReviewsTable venue={venue} {...tableProps} />,
      },
    ],
    [tableProps, venue],
  );

  return (
    <SummaryTableStyled
      onRetry={reviewsInsights.refetch}
      isError={reviewsInsights.isError || (reviewsInsights.isLoading && !reviewsInsights.isFetching)}
      isFetching={reviewsInsights.isRefetching}
      link={<RatingsFilterPopover filters={filters} setFilters={setFilters} />}
      isLinkVisible={!!reviewsInsights.data}
      title={intl.formatMessage({
        // todo @ivanmartynov use 'insights.ratings-and-reviews.reviews-table-title-with-counter' when MAXX-1473 is done
        id: 'insights.ratings-and-reviews.reviews-table-title',
      })}
      rows={rows}
    />
  );
};
